import React, { useState, useEffect, useRef } from 'react';
import { useFavouriteQuestionsAtom } from '../../../atoms/chat/useFavouriteQuestionsAtom';
import { useSelectedTabAtom } from '../../../atoms/chat/useSelectedTabAtom';
import { saveFavouriteQuestion, getFavouriteQuestions } from '../../../api/questionAPIs';
import { useTranslation } from 'react-i18next';
import { ALL_FOLDERS, useChatEnvironmentAtom } from '../../../atoms/chat/useChatEnvironmentAtom';
import { marked } from 'marked';
import { formatTimeWithDate } from '../../../services/formatTime';
import { useNotification } from '../../../context/NotificationContext';
import { NotificationType } from '../../../enums/NotificationType';

import MessageTable from './MessageTable';
import MessageChart from './MessageChart';
import MessageSourceModal from './MessageSourceModal';
import MessageReview from './MessageReview';
import useAuth from "../../../hooks/useAuth";
import config from '../../../config/configJS';
import ImageGallery from '../image/ImageGallery';
import DOMPurify from 'dompurify';
import hljs from 'highlight.js';
import "highlight.js/styles/magula.css";

const MessageBubble = (props) => {
  const { auth } = useAuth();
  let userName = auth.ufn;
  // fotka prihláseného používateľa (len AD login) – použije sa namiesto avatara pri jeho správach
  const userPhoto = (props.role !== 'assistant' && auth.uam === 'ad') ? localStorage.getItem('userPhoto') : null;
  const [t, i18n] = useTranslation('global');

  const [chatEnvironmentId, ] = useChatEnvironmentAtom();
  const [, setFavQuestions] = useFavouriteQuestionsAtom();
  const [selectedTab] = useSelectedTabAtom();
  const { showNotification } = useNotification();

  const [sourcesAvailable, setSourcesAvailable] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const [revealCount, setRevealCount] = useState(props.animate ? 0 : null);
  const rootRef = useRef(null);

  // Typewriter efekt pre streamovanú odpoveď (type 3) – postupné odkrývanie textu
  useEffect(() => {
    if (!props.animate || props.type !== 3 || typeof props.message !== 'string') {
      return;
    }
    const full = props.message;
    const step = Math.max(2, Math.ceil(full.length / 120));
    let i = 0;
    setRevealCount(0);
    const timer = setInterval(() => {
      i = Math.min(full.length, i + step);
      setRevealCount(i);
      if (i >= full.length) {
        clearInterval(timer);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [props.animate, props.message, props.type]);

  // Pri spustení renderu streamovanej odpovede posuň jej začiatok RAZ do hornej polovice okna (~40 %),
  // nie úplne k vrchu – chat sa posunie hore len o kúsok a písať sa začne "od stredu". Ďalej už
  // nescrollujeme; dlhá odpoveď rastie nadol až za spodný okraj / za input. Krátku drží na mieste
  // rezerva výšky (.msg-stream-reserve), takže neskáče.
  const didPositionRef = useRef(false);
  useEffect(() => {
    if (didPositionRef.current) return;
    // streamovaná odpoveď: dokumenty (type 3) aj všeobecný chat (type 0)
    if (!(((props.animate && props.type === 3) || props.streaming) && rootRef.current)) return;
    // Spustí sa RAZ – pri prvom tokene, keď sa loading bublina zmení na streamovanú odpoveď.
    didPositionRef.current = true;
    const el = rootRef.current;
    const scroller = el.closest('.context-wrap');
    const timer = setTimeout(() => {
      if (!scroller) {
        el.scrollIntoView({ block: 'start', behavior: 'smooth' });
        return;
      }
      const elRect = el.getBoundingClientRect();
      const scRect = scroller.getBoundingClientRect();
      // začiatok odpovede vyššie (~28 % výšky viditeľnej časti chatu), nech je hneď vidno prvé riadky
      const target = scroller.scrollTop + (elRect.top - scRect.top) - scroller.clientHeight * 0.28;
      scroller.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
    }, 50);
    return () => clearTimeout(timer);
  }, [props.animate, props.streaming, props.type]);

  const isAnimating = props.animate && props.type === 3 && typeof props.message === 'string' && revealCount !== null && revealCount < props.message.length;
  const displayedMessage = (props.animate && props.type === 3 && typeof props.message === 'string' && revealCount !== null)
    ? props.message.slice(0, revealCount)
    : props.message;

  const toggleSources = () => {
    setShowSources(!showSources);
  };

  const handleIconClick = () => {
    const lang = i18n.language;
    // pri „Všetky moje priečinky“ sa otázka uloží bez priečinka
    saveFavouriteQuestion(selectedTab, props.message, lang, chatEnvironmentId === ALL_FOLDERS ? null : chatEnvironmentId)
      .then(() => getFavouriteQuestions(selectedTab, lang))
      .then((questions) => {
        setFavQuestions(questions || []);
        showNotification({ message: t('questionSaved'), type: NotificationType.Success });
      })
      .catch((err) => {
        console.error('Error saving question:', err);
        showNotification({ message: t('errorOccured'), type: NotificationType.Error });
      });
  };

  useEffect(() => {
    setSourcesAvailable(Array.isArray(props.meta) && props.meta.length > 0);
    console.log('propsType: ', props.type);
  }, [props.meta]);

  const processText = (text) => {
    if (text === undefined) {
      return '';
    }
    
    const textWithLinks = replaceHttpTextWithLink(text);
    
    const markdownHtml = marked.parse(textWithLinks);
    
    const sanitizedHtml = DOMPurify.sanitize(markdownHtml);
  
    // Trim any excessive whitespace around the html to prevent stretching
    return forceLinksToNewWindow(sanitizedHtml).trim();
  };

  const forceLinksToNewWindow = (html) => {
    if (!html) {
      return '';
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const links = doc.querySelectorAll('a');

    links.forEach((link) => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });

    return doc.body.innerHTML;
  };

  const replaceHttpTextWithLink = (text) => {
    if (text == undefined) {
      return text;
    }

    // starting with http/https
    const regex1 = /((http|https):\/\/[^\s.]+(\.[^\s.]+)*)(\b|(?=\s)|(?=\.$))/ig;
    if (text.match(regex1)) {
      return text.replace(regex1, (url) => {
        return `<a href="${url}" target="_blank">${url}</a>`;
      });
    }

    //starting with www
    const regex2 = /(^|[^\/])(www\.[^\s.]+(\.[^\s.]+)*(\b|(?=\s)|$))/ig;
    if (text.match(regex2)) {
      return text.replace(regex2, (match, p1, url) => {
        return `${p1}<a href="https://${url}" target="_blank">${url}</a>`;
      });
    }
    
    return text;
  }

  const getFileNameFromUrl = (url) => {
    return url.substring(url.lastIndexOf('/') + 1);
  };

  function cleanSqlString(sqlString) {
    if(sqlString == undefined){
      return "";
    }
    // Regular expression to remove backticks, 'sql', and whitespace at the start and end
    const cleanedString = sqlString.replace(/```sql\s*|\s*```/g, '').trim();
    return cleanedString;
  }

  const highlightCode = (props) => {
      try {
          if (props.role === "assistant" && props.type === 0 && props.message !== undefined) {
              // Adjust regex to match all occurrences of the code block pattern
              const regex = /```(\w+)?\s*([\s\S]*?)\s*```/g;
              const matches = [...props.message.matchAll(regex)];
              
              if (matches.length === 0) {
                  // No code blocks, just process the entire message as markdown
                  return processText(props.message);
              }

              // Extract code blocks and replace them with placeholders
              let processedMessage = props.message;
              const codeBlocks = [];
              
              matches.forEach((match, index) => {
                  const placeholder = `CODE_BLOCK_PLACEHOLDER_${index}`;
                  codeBlocks.push({
                      placeholder: placeholder,
                      language: match[1] || '',
                      code: match[2],
                      original: match[0]
                  });
                  processedMessage = processedMessage.replace(match[0], placeholder);
              });
              
              // Process the text with markdown (excluding code blocks)
              let markdownProcessed = processText(processedMessage);
              
              // Replace placeholders with highlighted code blocks
              codeBlocks.forEach((block) => {
                  const highlightedCode = DOMPurify.sanitize(hljs.highlightAuto(block.code).value);
                  markdownProcessed = markdownProcessed.replace(
                      block.placeholder, 
                      `<pre><code>${highlightedCode}</code></pre>`
                  );
              });
              
              return markdownProcessed;
          } else {
              return processText(props.message);
          }
      } catch (ex) {
          console.error("Error processing code highlighting:", ex);
          return processText(props.message);
      }
  };

  // Pozbiera image GUID-y zo všetkých zdrojov (userDocsMessages) — podporuje camelCase aj PascalCase + fallback na pageImageData.
  // Ku každému GUID-u priečinok jeho zdroja (DocsEnvId) — obrázok sa načíta z priečinka zdroja, nie z výberu v chate.
  const imageEnvByGuid = {};
  const getImageGuids = (meta) => {
    if (!Array.isArray(meta)) return '';
    const parts = [];
    meta.forEach((source) => {
      const m = source?.metadata || source?.Metadata || {};
      const envId = source?.DocsEnvId ?? source?.docsEnvId;
      const guids = [];
      if (m.page_image_guids) {
        guids.push(m.page_image_guids);
      } else {
        const pid = source?.pageImageData || source?.PageImageData;
        if (pid && typeof pid === 'object') {
          Object.values(pid).forEach((v) => { if (v) guids.push(v); });
        }
      }
      guids.join(',').split(',').map((x) => x.trim()).filter(Boolean).forEach((g) => {
        if (envId != null && imageEnvByGuid[g] == null) imageEnvByGuid[g] = envId;
      });
      parts.push(...guids);
    });
    return [...new Set(parts.join(',').split(',').map((x) => x.trim()).filter(Boolean))].join(',');
  };
  const imageGuids = config.imgAllowed ? getImageGuids(props.meta) : '';

  return (
    <>
      <div
        ref={rootRef}
        className={`msg ${props.role === 'assistant' ? 'left-msg' : 'right-msg'}`}
      >
        <div>
          <img
            alt={props.role}
            src={props.role === 'assistant' ? props.bot : (userPhoto || props.avatar)}
            className="msg-img"
          />
        </div>

        <div className="msg-bubble">
          {props.type !== 'loading' ? (
            <div className="msg-info">
              <div className="msg-info-name">
                {props.role === 'assistant' ? 'Millada' : props.role === 'user' ? userName : null}
              </div>
              {props.role !== 'assistant' && props.type === 0 && typeof props.message === 'string' && (
                <div className="msg-favorite" onClick={handleIconClick} title={t('save') || 'Uložiť'}>
                  <span className="material-symbols-outlined">add_circle</span>
                </div>
              )}
              <div className="msg-info-time">{formatTimeWithDate(props.sent)}</div>
            </div>
          ) : null}
          { props.type === 0 || props.type === 4 || props.type === 'stopping' ? (
                props.role === "assistant" ? (
                <>
                  <div className="chat-text" dangerouslySetInnerHTML={{__html: highlightCode(props) }}/>
                  {props.generatedSQL && <div className="sql-wrapper">{cleanSqlString(props.generatedSQL)}</div>}
                </>
              ) : (
                <>
                  <div dangerouslySetInnerHTML={{__html: processText(props.message) }}/>
                </>
              )
          ) : props.type === 1 ? (
            <MessageTable id={props.id} columns={props.message.Columns} rows={props.message.Rows} generatedSQL={props.generatedSQL} />
          ) : props.type === 2 ? (
            <MessageChart id={props.id} json={props.message} /> 
          ): props.type === 3 ? (           
            <>
            <div className="msg-text-container">
              <div className="msg-text"
                  dangerouslySetInnerHTML={{__html: processText(displayedMessage)}}>
              </div>
            </div>

            {!isAnimating && !props.streaming && config.imgAllowed && imageGuids ?
             <div className="img-container">
                <ImageGallery idString={imageGuids} envByGuid={imageEnvByGuid} />
             </div>
            : null }

            {!isAnimating && !props.streaming && sourcesAvailable && (
              <>
              <div className="show-sources">
              <div className="show-sources-label" onClick={toggleSources}>{t('showSources')}</div>
                <span className={showSources ? "material-symbols-outlined show-sources-icon rotate0 rotate180" : "material-symbols-outlined show-sources-icon rotate0"} onClick={toggleSources}>
                keyboard_arrow_down
                </span>
              </div>

              <div className={showSources ? "sources-list height8em" : "height0"}>
                {showSources && (
                  props.meta.map((source, index) => {
                    const srcMeta = source.Metadata || source.metadata || {};
                    const chunk = source.Chunk ?? source.chunk ?? '';
                    const extractionTaskId = source.ExtractionTaskId ?? source.extractionTaskId;
                    const urlSource = srcMeta.url_source;
                    // SharePoint zdroje majú url_source (odkaz na SharePoint) → zobraz odkaz miesto downloadu.
                    // Nezávisí to od envType (ten DTO nemusí niesť); riadi to prítomnosť url_source.
                    return (
                    <div className='msg-source-item' key={index}>
                        <MessageSourceModal
                          meta={{
                            f: urlSource ? getFileNameFromUrl(urlSource) : (srcMeta.f ?? srcMeta.fileName ?? ''),
                            n: srcMeta.n ?? srcMeta.number ?? '',
                            p: srcMeta.p ?? srcMeta.page ?? '',
                            c: chunk,
                            s: urlSource ? urlSource + '?web=1' : null,
                            folder: source.FolderName ?? source.folderName ?? null   // hľadanie vo všetkých priečinkoch
                          }}
                          extractionTaskId={extractionTaskId}
                        />
                    </div>
                    );
                  })
                )}
              </div>
              </>
            )} 

            {/* zastavená (neuložená) odpoveď nemá messageId -> hodnotenie by nemalo kam zapísať */}
            {!isAnimating && !props.streaming && config.msgReviewsAllowed && props.messageId ?
              <MessageReview id={props.messageId} rating={props.rating} />
            : null }
            </>
          ) : props.type === 'loading' ? (
            <div className="msg-text">
              {props.progress ? (
                <div 
                  className="loading-progress" 
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85em', opacity: 0.8, marginBottom: '6px' }}
                >
                  <span>{props.progress}</span>
                  {props.stepCount && props.stepCount > 1 ? (
                    <span>
                      {t('step')} {props.stepCount}
                    </span>
                  ) : null}
                </div>
              ) : null}
              <div className="typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          ) : null }
        </div>
      </div>
    </>
  );
};

export default MessageBubble;
